import type { ReactNode } from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { BlurView } from "expo-blur";
import { semanticColors } from "../theme/tokens";
import { compactSecondarySurfaceStyle } from "./CompactActionButton";

export type IconButtonSurface = "bare" | "soft" | "frosted" | "destructive";
export type IconButtonSize = "xs" | "sm" | "md" | "lg" | number;

export type IconButtonProps = Omit<
  PressableProps,
  "children" | "style" | "accessibilityLabel"
> & {
  accessibilityLabel: string;
  children: ReactNode;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  size?: IconButtonSize;
  surface?: IconButtonSurface;
  radius?: number;
  backgroundColor?: string;
  pressedBackgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  disabledOpacity?: number;
  pressedOpacity?: number;
  badge?: boolean;
  badgeColor?: string;
  badgeSize?: number;
  badgeTop?: number;
  badgeRight?: number;
  badgeStyle?: StyleProp<ViewStyle>;
  style?: PressableProps["style"];
};

const SIZE_VALUE: Record<Exclude<IconButtonSize, number>, number> = {
  xs: 26,
  sm: 28,
  md: 30,
  lg: 36,
};

const SURFACE_BACKGROUND: Record<
  IconButtonSurface,
  { background: string; pressed: string }
> = {
  bare: { background: "transparent", pressed: "transparent" },
  /** The tonal fill shared with the secondary and compact primary buttons. */
  soft: {
    background: semanticColors.action.tonal.background,
    pressed: semanticColors.action.tonal.pressedBackground,
  },
  /**
   * Frosted glass for round buttons on light glass cards: the same material
   * as the compact secondary button, with its white rim and lit top edge.
   */
  frosted: {
    background: "rgba(255, 255, 255, 0.46)",
    pressed: "rgba(255, 255, 255, 0.3)",
  },
  /**
   * The same round shape, tinted with the danger red: the destructive
   * secondary button's wash.
   */
  destructive: {
    background: semanticColors.action.destructive.background,
    pressed: semanticColors.action.destructive.pressedBackground,
  },
};

export function IconButton({
  action,
  accessibilityLabel,
  accessibilityRole,
  accessibilityState,
  onPress,
  children,
  size = "lg",
  surface = "bare",
  radius,
  backgroundColor,
  pressedBackgroundColor,
  borderColor = "transparent",
  borderWidth = 0,
  disabled = false,
  disabledOpacity = 0.45,
  pressedOpacity = 0.7,
  badge = false,
  badgeColor = "rgba(90,77,69,0.9)",
  badgeSize = 7,
  badgeTop = 6,
  badgeRight = 6,
  badgeStyle,
  style,
  ...pressableProps
}: IconButtonProps) {
  const dimension = getDimension(size);
  const isDisabled = disabled === true;
  const accessibilityDisabled =
    accessibilityState?.disabled == null
      ? isDisabled
      : accessibilityState.disabled === true;
  const defaultBackgroundColor = SURFACE_BACKGROUND[surface].background;
  const defaultPressedBackgroundColor = SURFACE_BACKGROUND[surface].pressed;
  const capturePress = usePressCapture("IconButton");

  return (
    <Pressable
      {...pressableProps}
      onPress={(event) => {
        capturePress(
          pressLabel(accessibilityLabel, pressableProps.testID),
          action,
        );
        onPress?.(event);
      }}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole ?? "button"}
      accessibilityState={{
        ...accessibilityState,
        disabled: accessibilityDisabled,
      }}
      disabled={isDisabled}
      style={(state) => [
        surface === "frosted" ? compactSecondarySurfaceStyle : null,
        {
          width: dimension,
          height: dimension,
          borderRadius: radius ?? dimension / 2,
          backgroundColor: state.pressed
            ? (pressedBackgroundColor ?? defaultPressedBackgroundColor)
            : (backgroundColor ?? defaultBackgroundColor),
          borderColor,
          borderWidth,
          alignItems: "center",
          justifyContent: "center",
          opacity: isDisabled
            ? disabledOpacity
            : state.pressed
              ? pressedOpacity
              : 1,
        },
        typeof style === "function" ? style(state) : style,
      ]}
    >
      {surface === "frosted" && Platform.OS !== "web" ? (
        <BlurView
          pointerEvents="none"
          intensity={24}
          tint="light"
          experimentalBlurMethod="dimezisBlurView"
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      {children}
      {badge ? (
        <View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: badgeTop,
              right: badgeRight,
              width: badgeSize,
              height: badgeSize,
              borderRadius: badgeSize / 2,
              backgroundColor: badgeColor,
            },
            badgeStyle,
          ]}
        />
      ) : null}
    </Pressable>
  );
}

function getDimension(size: IconButtonSize) {
  return typeof size === "number" ? size : SIZE_VALUE[size];
}

export default IconButton;
